import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";

import { apiVariable } from "../../../../api/apiVariable";
import useAuth from "../../../../hooks/useAuth";
import StatusSnackbar from "../../../General/StatusSnackbar/StatusSnackbar";
import GenericFormPopup from "../../GenericFormPopup/GenericFormPopup";
import {
  quickAccidentFieldConfig,
  quickAccidentInitialValues,
} from "../../../../formConfigs/Fleet/accidentFieldConfig";
import {
  fetchAllDrivers,
  fetchAssets,
} from "../../IncidentPopup/IncidentPopup";
import { getId } from "../../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import dayjs, { CHICAGO_TIMEZONE } from "../../../../utils/dayjsTimezone";
import { fetchOwnerCompanies } from "../../TruckPopup/TruckPopup";

const QuickAccidentPopup = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: drivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: open,
  });

  const { data: trucks } = useQuery({
    queryKey: ["assets", "Truck"],
    queryFn: () => fetchAssets("Truck", token),
    enabled: open,
  });

  const { data: trailers } = useQuery({
    queryKey: ["assets", "Trailer"],
    queryFn: () => fetchAssets("Trailer", token),
    enabled: open,
  });

  const { data: ownerCompanies } = useQuery({
    queryKey: ["ownerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: open,
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");

  const mutation = useMutation({
    mutationFn: (payload: any) =>
      axios.post(`${apiVariable}/accident`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["accidents"] });
      handleClose();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to submit accident");
    },
  });

  const submitHandler = (data: any) => {
    const finalData = {
      date: data.date
        ? dayjs.tz(data.date, CHICAGO_TIMEZONE).utc().toISOString()
        : null,
      location: data.location,
      truckId: getId(data.truckId),
      trailerId: getId(data.trailerId),
      company: data.company,
      driverId: getId(data.driverId),
      description: data.description,
      hasCitation: data.hasCitation,
      hasInjury: data.hasInjury,
      hasTowing: data.hasTowing,
      hasPoliceReport: data.hasPoliceReport,
    };

    mutation.mutate(finalData);
  };

  return (
    <>
      <Button variant="outlined" color="primary" onClick={handleOpen}>
        Quick Add Accident
      </Button>
      <GenericFormPopup
        formFields={quickAccidentFieldConfig}
        initialValues={quickAccidentInitialValues}
        title="Quick Add Accident"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={mutation.isPending}
        autocompleteData={{
          drivers: drivers || [],
          trucks: trucks || [],
          trailers: trailers || [],
          ownerCompanies: ownerCompanies || [],
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage="Accident added successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default QuickAccidentPopup;
